import React from "react"
import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import type { DrawerRootChangeEventDetails } from "@base-ui/react/drawer"
import { useMediaBreakpoint } from "../use-media-breakpoint"
import { DrawerProvider } from "./drawer-context"
import type { DrawerSide } from "./drawer-context"
import { DrawerTrigger } from "./drawer-trigger"
import { DrawerContent } from "./drawer-content"
import { DrawerHeader } from "./drawer-header"
import { DrawerTitle } from "./drawer-title"
import { DrawerDescription } from "./drawer-description"
import { DrawerBody } from "./drawer-body"
import { DrawerClose } from "./drawer-close"

export type { DrawerSide } from "./drawer-context"
export type { DrawerTriggerProps } from "./drawer-trigger"
export type { DrawerContentProps } from "./drawer-content"
export type { DrawerHeaderProps } from "./drawer-header"
export type { DrawerTitleProps } from "./drawer-title"
export type { DrawerDescriptionProps } from "./drawer-description"
export type { DrawerBodyProps } from "./drawer-body"
export type { DrawerCloseProps } from "./drawer-close"
export type { DrawerRootChangeEventDetails } from "@base-ui/react/drawer"

// =============================================================================
// Props
// =============================================================================

export type DrawerProps = {
  children?: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  /** Called when the open state changes */
  onOpenChange?: (
    open: boolean,
    eventDetails: DrawerRootChangeEventDetails
  ) => void
  /** Called once the open or close animation has finished */
  onOpenChangeComplete?: (open: boolean) => void
  /** Which edge the drawer enters from on wider viewports */
  side?: DrawerSide
}

// =============================================================================
// Root
// =============================================================================

/**
 * A overlay panel that slides above the page.
 *
 * Dismissed by the close button, backdrop click, the Escape key, or a swipe.
 *
 * Renders a bottom sheet until the `md` breakpoint, at which point it renders a
 * left or right (default) side panel.
 *
 * Compose with `Drawer.Trigger`, `Drawer.Content`, `Drawer.Header`,
 * `Drawer.Title`, `Drawer.Description`, `Drawer.Body` and `Drawer.Close`.
 *
 * @example
 * ```tsx
 * <Drawer open={open} onOpenChange={setOpen}>
 *   <Drawer.Content>
 *     <Drawer.Header>
 *       <Drawer.Title>Notifications</Drawer.Title>
 *       <Drawer.Close />
 *     </Drawer.Header>
 *     <Drawer.Body>{children}</Drawer.Body>
 *   </Drawer.Content>
 * </Drawer>
 * ```
 */
function DrawerRoot({
  children,
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  side = "right"
}: DrawerProps) {
  const isSidePanel = useMediaBreakpoint("md")
  const context = React.useMemo(
    () => ({ side, isSidePanel }),
    [side, isSidePanel]
  )

  return (
    <DrawerProvider value={context}>
      <DrawerPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
        swipeDirection={isSidePanel ? side : "down"}
      >
        {children}
      </DrawerPrimitive.Root>
    </DrawerProvider>
  )
}

DrawerRoot.displayName = "Drawer"

// =============================================================================
// Compound component
// =============================================================================

interface DrawerComponent {
  (props: DrawerProps): React.JSX.Element
  Trigger: typeof DrawerTrigger
  Content: typeof DrawerContent
  Header: typeof DrawerHeader
  Title: typeof DrawerTitle
  Description: typeof DrawerDescription
  Body: typeof DrawerBody
  Close: typeof DrawerClose
}

export const Drawer: DrawerComponent = Object.assign(DrawerRoot, {
  Trigger: DrawerTrigger,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Body: DrawerBody,
  Close: DrawerClose
} as const)
